// Tela de Cronograma por projeto — rota /admin/cronogramaConsole/:parentId (parentId = id do
// SmartFlowProject, igual a ProjectConsole/SmartFlowImporter). Reproduz as regras da planilha
// "Cronograma de Projetos - Golive.xlsx": tarefas em EAP com predecessoras/dias úteis, seções
// que só mostram Horas Orçadas/% Conclusão calculados (nunca digitados), e revisões (a mais
// recente é editável, as anteriores só consulta). Edição campo-a-campo — sem copiar/colar
// entre células como no Excel.
import React from "react"
import ReactBSAlert from "react-bootstrap-sweetalert"
import Moment from "moment"
import { Container, Row, Col, Card, CardBody, Table, Input, Button } from "reactstrap"

import SimpleHeader from "components/Headers/SimpleHeader.js"
import Loading from "components/Modals/Loading"
import api from "services/api"
import { isConsulting } from "services/auth"
import golivePrintLogo from "assets/img/brand/golive.png"

// O backend grava toda data como meia-noite UTC (ver comentário em cronogramaEngine.js). Se a
// gente formatasse em hora local, um navegador em fuso negativo (Brasil, UTC-3) mostraria o dia
// ANTERIOR — 2026-09-10T00:00:00Z vira 09/09 às 21h de Brasília. Por isso sempre Moment.utc(),
// nunca Moment() puro, em qualquer data que vem da API.
function fmtDate(d) {
  return d ? Moment.utc(d).format("DD/MM/YYYY") : "-"
}

function toInputDate(d) {
  return d ? Moment.utc(d).format("YYYY-MM-DD") : ""
}

function errorMessage(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.error) || err.message || fallback
}

function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[c]))
}

// Código (seq), EAP e Tarefa ficam congeladas à esquerda (mesmo efeito de "congelar painéis"
// do Excel/Sheets) — precisam de largura fixa pra o `left` de cada uma ser calculável. O
// cabeçalho inteiro também fica congelado no topo, então dá pra rolar a lista de ~150
// tarefas sem perder a referência de qual linha/coluna é qual.
const SEQ_COL_WIDTH = 45
const EAP_COL_WIDTH = 85
const TAREFA_COL_WIDTH = 330 // +50% do valor original (220), campo de nome da tarefa maior

const SEQ_COL_LEFT = 0
const EAP_COL_LEFT = SEQ_COL_LEFT + SEQ_COL_WIDTH
const TAREFA_COL_LEFT = EAP_COL_LEFT + EAP_COL_WIDTH

// Larguras fixas de TODAS as colunas (não só as 3 congeladas) — necessárias porque agora
// cabeçalho e corpo são duas <table> SEPARADAS (ver comentário grande mais abaixo sobre o
// motivo), e só ficam alinhadas coluna-a-coluna se as duas usarem exatamente o mesmo
// <colgroup>, com "table-layout: fixed" (senão cada tabela recalcularia as larguras sozinha
// a partir do próprio conteúdo, e as colunas do cabeçalho ficariam desalinhadas das do corpo).
const COL_WIDTHS = {
  duracao: 70,
  inicio: 90,
  inicioReal: 170,
  fim: 90,
  fimReal: 170,
  status: 140,
  percentConcl: 75,
  horas: 75,
  observacoes: 360, // +125% do valor original (160), sem a coluna Responsável
  pred1: 70,
  lag1: 60,
  pred2: 70,
  lag2: 60,
  acoes: 150,
}

// % Concluído por DURAÇÃO (dias), não por Horas — é o critério do % geral do Gantter/CSV. Folha =
// o próprio % digitado; seção/raiz = média dos % das atividades abaixo dela (todos os níveis)
// ponderada pela Duração de cada uma. Sem nenhuma duração (peso total 0) cai na média simples.
// O % ponderado por Horas (o rollup que o backend calcula) aparece no cabeçalho como "% Medido".
function computeDurationPercents(tasks) {
  const childrenOf = new Map()
  for (const t of tasks) {
    if (t.parentSection !== null && t.parentSection !== undefined) {
      if (!childrenOf.has(t.parentSection)) childrenOf.set(t.parentSection, [])
      childrenOf.get(t.parentSection).push(t.seq)
    }
  }
  const bySeq = new Map(tasks.map((t) => [t.seq, t]))
  const acc = new Map() // seq -> { weight, weighted, sumPct, count } das ATIVIDADES abaixo
  const percents = new Map()
  const visit = (seq) => {
    if (acc.has(seq)) return acc.get(seq)
    const task = bySeq.get(seq)
    const kids = childrenOf.get(seq) || []
    let a
    if (!task.isSection || kids.length === 0) {
      const w = task.durationDays || 0
      const pct = task.percentComplete || 0
      a = { weight: w, weighted: w * pct, sumPct: pct, count: 1 }
    } else {
      a = { weight: 0, weighted: 0, sumPct: 0, count: 0 }
      for (const k of kids) {
        const c = visit(k)
        a.weight += c.weight
        a.weighted += c.weighted
        a.sumPct += c.sumPct
        a.count += c.count
      }
    }
    acc.set(seq, a)
    percents.set(seq, a.weight > 0 ? a.weighted / a.weight : a.count > 0 ? a.sumPct / a.count : 0)
    return a
  }
  for (const t of tasks) visit(t.seq)
  return percents
}

function gridTotalWidth(canEdit, isClient) {
  const sum = Object.entries(COL_WIDTHS)
    .filter(([key]) => (canEdit || key !== "acoes") && !(isClient && key === "horas"))
    .reduce((total, [, width]) => total + width, 0)
  return SEQ_COL_WIDTH + EAP_COL_WIDTH + TAREFA_COL_WIDTH + sum
}

// Mesmo tom do .thead-light do Bootstrap — precisa ser um valor OPACO explícito aqui, porque
// "position: sticky" só tampa de verdade o conteúdo que rola por baixo se a própria célula
// tiver fundo próprio; contando só com a classe CSS, em alguns navegadores a célula sticky vira
// "vazada" e a linha de baixo aparece por trás dela durante o scroll.
const HEADER_BG = "#e9ecef"

// Testado e comprovado: "position: sticky" no EIXO VERTICAL dentro de <table> (seja por célula,
// seja no <thead> inteiro) tem um bug real de repintura do Chrome — em scroll rápido, uma linha
// do corpo "vence a corrida" e aparece por cima do cabeçalho por alguns frames. Não é um erro de
// CSS corrigível com mais ajuste fino; é um bug de timing do compositor, reproduzido de novo
// mesmo depois de mover o sticky pro <thead>. A solução que não depende de sticky vertical
// nenhum: cabeçalho e corpo viram duas <table> SEPARADAS (ver o grid mais abaixo) — a do
// cabeçalho fica FORA do contêiner que rola verticalmente, então ela nunca "gruda", porque nunca
// teve de onde se mover. O scroll horizontal das duas é sincronizado por JS (this.headerRef.
// scrollLeft = corpo.scrollLeft), técnica padrão de grid com cabeçalho fixo sem apoio em
// position:sticky. As 3 colunas congeladas (Cód/EAP/Tarefa) continuam com sticky, mas só no eixo
// horizontal (left) — sticky raramente vertical é o eixo problemático; nunca reproduzimos o bug
// só com left.
const GPU_LAYER = { transform: "translateZ(0)" }

const headerCellStyle = (extra) => ({
  background: HEADER_BG,
  ...extra,
})
const stickyColStyle = (left, width, background, extra) => ({
  position: "sticky",
  left,
  width,
  minWidth: width,
  ...GPU_LAYER,
  background,
  zIndex: 1,
  ...extra,
})

class CronogramaConsole extends React.Component {
  state = {
    loading: true,
    loadError: null,
    project: null,
    version: null,
    isCurrentVersion: true,
    tasks: [],
    revisions: [],
    collapsed: {},
    alert: null,
    busy: false,
    scrollbarWidth: 0,
    // Recolher o card de cabeçalho (Projeto/Cliente/Revisão/etc.) só esconde esse bloco fixo de
    // informação — não encosta na grade nem na sincronização de scroll dela — e libera mais
    // altura de tela pra grade (ver maxHeight condicional mais abaixo).
    headerCollapsed: false,
    // Data de início usada ao criar um cronograma do zero (a tela de "sem cronograma ainda").
    blankStartDate: Moment().format("YYYY-MM-DD"),
    // Templates = cronogramas dos projetos do cliente GoLive (ver /cronogramaVersion/templates);
    // só carregados quando o projeto ainda não tem cronograma.
    templates: [],
    templateProject: "",
  }

  headerScrollRef = React.createRef()
  bodyScrollRef = React.createRef()

  componentDidMount() {
    this.load(this.props.match.params.parentId)
    window.addEventListener("resize", this.measureScrollbar)
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.measureScrollbar)
  }

  componentWillReceiveProps(nextProps) {
    const currentProjectId = this.state.project && this.state.project._id
    if (nextProps.match.params.parentId !== currentProjectId) {
      this.load(nextProps.match.params.parentId)
    }
  }

  componentDidUpdate() {
    this.measureScrollbar()
  }

  // Largura real da barra de rolagem vertical do corpo (0 no Mac com "mostrar barras: ao rolar",
  // que é o padrão — mas pode ser ~15px em outros SOs/configurações). Sem compensar isso, o
  // cabeçalho (que nunca tem barra, porque não rola verticalmente) ficaria alguns pixels mais
  // largo que o corpo, e as colunas saem de alinhamento na ponta direita da grade.
  measureScrollbar = () => {
    const el = this.bodyScrollRef.current
    if (!el) return
    const width = el.offsetWidth - el.clientWidth
    if (width !== this.state.scrollbarWidth) this.setState({ scrollbarWidth: width })
  }

  // Cabeçalho não tem scroll próprio (fica fora do contêiner que rola) — reflete o scrollLeft
  // do corpo por JS a cada evento de scroll, no lugar de position:sticky (ver comentário grande
  // perto de GPU_LAYER sobre o bug do Chrome que essa troca resolve).
  handleBodyScroll = (e) => {
    if (this.headerScrollRef.current) {
      this.headerScrollRef.current.scrollLeft = e.target.scrollLeft
    }
  }

  notify = (message, type = "info") => {
    const color = { success: "success", danger: "danger", warning: "warning", info: "info" }[type] || "info"
    this.setState({
      alert: (
        <ReactBSAlert
          {...(type === "success" ? { success: true } : type === "danger" ? { danger: true } : { warning: true })}
          style={{ display: "block" }}
          title=""
          onConfirm={() => this.setState({ alert: null })}
          onCancel={() => this.setState({ alert: null })}
          confirmBtnBsStyle={color}
          confirmBtnText="Ok"
          btnSize=""
        >
          {message}
        </ReactBSAlert>
      ),
    })
  }

  // Mesmo padrão de confirmação usado no resto do SmartFlow (ver PlanConsole.js/Users.js):
  // ReactBSAlert com 2 botões — nunca window.confirm() nativo, que nem sempre dispara de
  // forma confiável dependendo do navegador/automação, e destoa visualmente do resto da tela.
  confirmAction = (message, actionLabel, onConfirmed) => {
    this.setState({
      alert: (
        <ReactBSAlert
          warning
          style={{ display: "block" }}
          title="Tem certeza?"
          onConfirm={() => this.setState({ alert: null })}
          onCancel={() => {
            this.setState({ alert: null })
            onConfirmed()
          }}
          showCancel
          confirmBtnBsStyle="secondary"
          confirmBtnText="Cancelar"
          cancelBtnBsStyle="danger"
          cancelBtnText={actionLabel}
          btnSize=""
        >
          {message}
        </ReactBSAlert>
      ),
    })
  }

  load = async (projectId) => {
    this.setState({ loading: true, loadError: null })
    try {
      const { data: project } = await api.get("/smartflowproject/read/" + projectId)
      const { data: current } = await api.get("/cronogramaVersion/getCurrent/" + projectId)
      const { data: revisions } = await api.post("/cronogramaVersion/search", {
        project: projectId,
        deleted: "N",
      })
      revisions.sort((a, b) => b.revisionNumber - a.revisionNumber)

      // Sem cronograma ainda: busca os templates pra tela de criação. Falha aqui não derruba a
      // tela — sem templates ela continua oferecendo o cronograma em branco.
      let templates = []
      if (!current.version && isConsulting()) {
        try {
          const { data } = await api.get("/cronogramaVersion/templates")
          templates = data
        } catch (err) {
          templates = []
        }
      }

      this.setState({
        templates,
        templateProject: templates.length ? templates[0].project : "",
        project,
        version: current.version,
        tasks: current.tasks,
        isCurrentVersion: true,
        revisions,
        collapsed: {},
        loading: false,
      })
    } catch (err) {
      // Importante: NUNCA renderizar a tela de "criar a partir do modelo" quando o problema
      // foi uma falha de rede/servidor, senão parece que o cronograma sumiu (já aconteceu:
      // usuário via a tela de criação de novo mesmo depois de já ter criado antes). Erro de
      // carregamento é um estado próprio, distinto de "realmente não existe cronograma ainda".
      this.setState({ loading: false, loadError: errorMessage(err, "erro desconhecido") })
    }
  }

  loadTasksForVersion = async (versionId) => {
    const { data: tasks } = await api.post("/cronogramaTask/search", {
      cronogramaVersion: versionId,
    })
    this.setState({ tasks })
  }

  reloadCurrentTasks = async () => {
    if (this.state.version) await this.loadTasksForVersion(this.state.version._id)
  }

  handleSelectRevision = async (versionId) => {
    const version = this.state.revisions.find((v) => v._id === versionId)
    const isCurrentVersion = this.state.revisions[0] && this.state.revisions[0]._id === versionId
    this.setState({ version, isCurrentVersion, collapsed: {} })
    await this.loadTasksForVersion(versionId)
  }

  createFromTemplate = async () => {
    this.setState({ busy: true })
    try {
      await api.post("/cronogramaVersion/create", {
        project: this.state.project._id,
        fromTemplate: true,
        templateProject: this.state.templateProject,
        startDate: this.state.blankStartDate,
      })
      await this.load(this.state.project._id)
      this.notify("Cronograma criado a partir do template.", "success")
    } catch (err) {
      this.notify("Não foi possível criar o cronograma: " + errorMessage(err), "danger")
    } finally {
      this.setState({ busy: false })
    }
  }

  createBlank = async () => {
    const { blankStartDate } = this.state
    if (!blankStartDate) {
      this.notify("Informe a data de início do projeto.", "warning")
      return
    }
    this.setState({ busy: true })
    try {
      await api.post("/cronogramaVersion/create", {
        project: this.state.project._id,
        blank: true,
        startDate: blankStartDate,
      })
      await this.load(this.state.project._id)
      this.notify('Cronograma criado. Use o "+" da linha para inserir as tarefas.', "success")
    } catch (err) {
      this.notify("Não foi possível criar o cronograma: " + errorMessage(err), "danger")
    } finally {
      this.setState({ busy: false })
    }
  }

  createNewRevision = () => {
    this.confirmAction(
      "Criar uma nova revisão a partir da atual? A revisão de hoje continua disponível como histórico.",
      "Sim, criar nova revisão",
      async () => {
        this.setState({ busy: true })
        try {
          await api.post("/cronogramaVersion/create", {
            project: this.state.project._id,
            cloneFrom: this.state.version._id,
          })
          await this.load(this.state.project._id)
          this.notify("Nova revisão criada.", "success")
        } catch (err) {
          this.notify("Não foi possível criar a revisão: " + errorMessage(err), "danger")
        } finally {
          this.setState({ busy: false })
        }
      }
    )
  }

  updateVersionField = async (field, value) => {
    try {
      const { data: version } = await api.put(
        "/cronogramaVersion/update/" + this.state.version._id,
        { [field]: value }
      )
      this.setState({ version })
      await this.reloadCurrentTasks()
    } catch (err) {
      this.notify("Não foi possível atualizar: " + errorMessage(err), "danger")
    }
  }

  saveTaskField = async (task, field, value) => {
    try {
      await api.put("/cronogramaTask/update/" + task._id, { [field]: value })
      await this.reloadCurrentTasks()
    } catch (err) {
      this.notify("Não foi possível salvar a alteração: " + errorMessage(err), "danger")
      await this.reloadCurrentTasks()
    }
  }

  insertTaskAfter = async (task) => {
    const parentSection = task.isSection ? task.seq : task.parentSection ?? null
    try {
      await api.post("/cronogramaTask/insertAfter", {
        cronogramaVersion: this.state.version._id,
        afterSeq: task.seq,
        task: { name: "Nova tarefa", parentSection, durationDays: 1 },
      })
      await this.reloadCurrentTasks()
    } catch (err) {
      this.notify("Não foi possível inserir a tarefa: " + errorMessage(err), "danger")
    }
  }

  deleteTask = (task) => {
    this.confirmAction(`Remover a tarefa "${task.name}"?`, "Sim, remover", async () => {
      try {
        await api.delete("/cronogramaTask/delete/" + task._id)
        await this.reloadCurrentTasks()
      } catch (err) {
        this.notify("Não foi possível remover a tarefa: " + errorMessage(err), "danger")
      }
    })
  }

  moveTask = async (task, direction) => {
    try {
      await api.post("/cronogramaTask/move", {
        cronogramaVersion: this.state.version._id,
        seq: task.seq,
        direction,
      })
      await this.reloadCurrentTasks()
    } catch (err) {
      this.notify("Não foi possível mover a tarefa: " + errorMessage(err), "danger")
    }
  }

  toggleCollapse = (seq) => {
    this.setState((prev) => ({ collapsed: { ...prev.collapsed, [seq]: !prev.collapsed[seq] } }))
  }

  // Recolhe TODAS as seções de uma vez, em qualquer nível de EAP — não só as de primeiro
  // nível, senão uma seção recolhida dentro de outra já recolhida ficaria "presa" expandida
  // assim que a mãe fosse reaberta.
  collapseAll = () => {
    const allSectionSeqs = this.state.tasks.filter((t) => t.isSection).map((t) => t.seq)
    this.setState({ collapsed: Object.fromEntries(allSectionSeqs.map((seq) => [seq, true])) })
  }

  expandAll = () => {
    this.setState({ collapsed: {} })
  }

  // Sobe pela cadeia de Seção-mãe; se qualquer ancestral estiver recolhido, a linha some —
  // igual ao agrupamento de tópicos do Excel/Sheets (Leia-me: "recolher tudo de uma vez").
  isVisible = (task, bySeq) => {
    let current = task
    while (current && current.parentSection !== null && current.parentSection !== undefined) {
      if (this.state.collapsed[current.parentSection]) return false
      current = bySeq.get(current.parentSection)
    }
    return true
  }

  // Gera uma folha de impressão à parte (janela nova) em vez de tentar imprimir a própria
  // grade — a grade tem cabeçalho/colunas fixos via scroll sincronizado por JS (ver o
  // comentário grande perto de GPU_LAYER), o que não faz sentido nenhum numa página impressa.
  // Só entram as linhas VISÍVEIS no momento (this.isVisible, a mesma função que decide o que
  // aparece na tela) — uma seção recolhida sai recolhida também na impressão, como pedido.
  // Início/Fim usam effectiveStart/effectiveEnd, os mesmos campos que a tela mostra e que já
  // carregam Início Real/Fim Real (inclusive a cascata quando fixados numa seção) — nenhuma
  // lógica de data nova aqui, só reaproveita o que o backend já resolveu.
  printCronograma = () => {
    const { project, version, tasks, revisions } = this.state
    const bySeq = new Map(tasks.map((t) => [t.seq, t]))
    const visibleTasks = tasks.filter((t) => this.isVisible(t, bySeq))
    const durationPercents = computeDurationPercents(tasks)

    const predText = (task) => {
      const parts = []
      if (task.pred1) parts.push(task.lag1 ? `${task.pred1}+${task.lag1}d` : `${task.pred1}`)
      if (task.pred2) parts.push(task.lag2 ? `${task.pred2}+${task.lag2}d` : `${task.pred2}`)
      return parts.join(", ")
    }

    const rows = visibleTasks
      .map((task) => {
        const depth = Math.max((task.eap || "").split(".").length - 1, 0)
        const duration = task.effectiveDuration ?? task.durationDays
        return `
          <tr class="${task.isSection ? "section" : ""}">
            <td class="num">${task.seq}</td>
            <td class="num">${escapeHtml(task.eap || "")}</td>
            <td style="padding-left:${8 + depth * 16}px">${escapeHtml(task.name || "")}</td>
            <td class="num">${duration != null ? `${duration}d` : "-"}</td>
            <td class="num">${Math.round(durationPercents.get(task.seq) ?? task.percentComplete ?? 0)}%</td>
            <td class="num">${fmtDate(task.effectiveStart)}</td>
            <td class="num">${fmtDate(task.effectiveEnd)}</td>
            <td class="num">${escapeHtml(predText(task))}</td>
          </tr>`
      })
      .join("")

    const isCurrent = revisions[0] && version && revisions[0]._id === version._id
    const revisionLabel = version ? `Revisão ${version.revisionNumber}${isCurrent ? " (atual)" : ""}` : ""
    const generatedAt = Moment().format("DD/MM/YYYY HH:mm:ss")
    const docTitle = `Cronograma - ${project ? project.name : ""}`

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(docTitle)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #222; margin: 20px; }
  .header { display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid #333; padding-bottom: 8px; margin-bottom: 6px; }
  .header .brand { display: flex; align-items: center; gap: 12px; }
  .header img { height: 34px; }
  .header .title { font-size: 14px; font-weight: bold; }
  .header .subtitle { font-size: 10px; color: #555; }
  .header .meta { text-align: right; font-size: 10px; color: #555; white-space: nowrap; }
  table { border-collapse: collapse; margin-top: 6px; table-layout: fixed; }
  th, td { border: 1px solid #ccc; padding: 3px 6px; font-size: 10px; overflow: hidden; word-wrap: break-word; }
  th { background: #f0f0f0; text-align: left; }
  td.num { white-space: nowrap; }
  tr.section td { font-weight: bold; background: #f7f9fc; }
  .footer { margin-top: 10px; font-size: 9px; color: #888; display: flex; justify-content: space-between; }
  @media print {
    @page { size: A4 landscape; margin: 12mm; }
    .footer { position: fixed; bottom: 0; left: 0; right: 0; }
  }
</style>
</head>
<body>
  <div class="header">
    <div class="brand">
      <img src="${golivePrintLogo}" alt="Golive" />
      <div>
        <div class="title">${escapeHtml(docTitle)}${project && project.customerShortName ? ` (${escapeHtml(project.customerShortName)})` : ""}</div>
        <div class="subtitle">${escapeHtml(revisionLabel)}</div>
      </div>
    </div>
    <div class="meta">Gerado em ${generatedAt}</div>
  </div>
  <table>
    <colgroup>
      <col style="width:24px" />
      <col style="width:36px" />
      <col style="width:260px" />
      <col style="width:36px" />
      <col style="width:33px" />
      <col style="width:42px" />
      <col style="width:42px" />
      <col style="width:54px" />
    </colgroup>
    <thead>
      <tr>
        <th>Cód.</th>
        <th>EAP</th>
        <th>Tarefa</th>
        <th>Duração</th>
        <th>% Concl.</th>
        <th>Início</th>
        <th>Fim</th>
        <th>Predecessores</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="footer">
    <span>${escapeHtml(docTitle)}</span>
    <span>Gerado em ${generatedAt}</span>
  </div>
</body>
</html>`

    const printWindow = window.open("", "_blank")
    if (!printWindow) {
      this.notify("O navegador bloqueou a janela de impressão — permita pop-ups pra este site.", "warning")
      return
    }
    printWindow.document.open()
    printWindow.document.write(html)
    printWindow.document.close()
    printWindow.onload = () => {
      printWindow.focus()
      printWindow.print()
    }
  }

  render() {
    const {
      loading,
      loadError,
      project,
      version,
      tasks,
      revisions,
      isCurrentVersion,
      alert,
      busy,
      scrollbarWidth,
      headerCollapsed,
    } =
      this.state
    const canEdit = isConsulting() && isCurrentVersion
    // Cliente não deve ver Horas (nem a coluna nem o total no cabeçalho) — só quem é da
    // consultoria acompanha esforço em horas; o cliente acompanha o % Concluído.
    const isClient = !isConsulting()
    const gridWidth = gridTotalWidth(canEdit, isClient)
    const bySeq = new Map(tasks.map((t) => [t.seq, t]))
    const childrenSeqs = new Set(tasks.map((t) => t.parentSection).filter((s) => s !== null && s !== undefined))
    const visibleTasks = tasks.filter((t) => this.isVisible(t, bySeq))
    const root = tasks.find((t) => t.parentSection === null || t.parentSection === undefined)

    // Medido = soma, de cada ATIVIDADE (nunca de seção — senão contaria em dobro, já que a
    // seção só reflete os filhos), de Horas × % Concluído — são as horas do total orçado que já
    // viraram trabalho de fato entregue. Dividindo por 100 porque % Concluído vem em 0–100, não
    // em fração; o resultado some corretamente com Horas (medidoHoras ≤ root.budgetedHours).
    const medidoHoras = tasks
      .filter((t) => !t.isSection)
      .reduce((sum, t) => sum + ((t.budgetedHours || 0) * (t.percentComplete || 0)) / 100, 0)

    // % Concluído (por Duração) — ver computeDurationPercents. Do cabeçalho = o da raiz.
    const durationPercents = computeDurationPercents(tasks)
    const percentByDuration = root ? durationPercents.get(root.seq) || 0 : 0

    // Espelha cronogramaEngine.findForcedDate: a seção mais externa com a data preenchida
    // vale pra todos os descendentes — então Início/Fim Real da própria tarefa fica sem efeito.
    // Exceto se a tarefa já está 100% concluída: fato histórico consumado não é mexido pela
    // reprogramação em bloco (mesma trava do motor, pra não travar o campo na tela sem o
    // backend realmente estar forçando nada nela).
    const forcedByAncestor = (task, field) => {
      if ((task.percentComplete || 0) >= 100) return false
      let found = false
      let parentSeq = task.parentSection
      const seen = new Set()
      while (parentSeq !== null && parentSeq !== undefined && !seen.has(parentSeq)) {
        seen.add(parentSeq)
        const parent = bySeq.get(parentSeq)
        if (!parent) break
        if (parent[field]) found = true
        parentSeq = parent.parentSection
      }
      return found
    }


    return (
      <>
        {alert}
        {/* Recolher o cabeçalho esconde também essa faixa (topo da tela) — só sobra o card de
            Projeto/Cliente numa linha só, ver mais abaixo. Sem a faixa por trás, a margem
            negativa (mt--6, pensada pra "flutuar" o conteúdo por cima da faixa) fica pequena
            em vez de negativa, senão o conteúdo subiria até sair da tela. */}
        {!headerCollapsed && (
          <SimpleHeader
            name="Cronograma"
            items={[
              { level: "nav", name: project ? project.name : "Projeto" },
              { level: "nav", name: "Cronograma" },
            ]}
          />
        )}
        <Container className={headerCollapsed ? "mt-3" : "mt--6"} fluid>
          {loading ? (
            <Loading modalOpened={loading} />
          ) : loadError ? (
            <Card>
              <CardBody className="text-center">
                <p className="text-danger mb-3">
                  Não foi possível carregar o cronograma: {loadError}
                </p>
                <Button
                  color="secondary"
                  onClick={() => this.load(this.props.match.params.parentId)}
                >
                  Tentar novamente
                </Button>
              </CardBody>
            </Card>
          ) : !version ? (
            <Card>
              <CardBody className="text-center">
                <p>Este projeto ainda não tem um cronograma.</p>
                {isConsulting() && (
                  <>
                    <div className="d-inline-block text-center mb-3" style={{ width: 220 }}>
                      <small className="text-muted d-block mb-1">Data de início do projeto</small>
                      <Input
                        bsSize="sm"
                        type="date"
                        className="text-center"
                        value={this.state.blankStartDate}
                        onChange={(e) => this.setState({ blankStartDate: e.target.value })}
                      />
                    </div>
                    {this.state.templates.length > 0 && (
                      <div className="d-block mb-3">
                        <div className="d-inline-block text-center" style={{ width: 320 }}>
                          <small className="text-muted d-block mb-1">Template</small>
                          <Input
                            bsSize="sm"
                            type="select"
                            value={this.state.templateProject}
                            onChange={(e) => this.setState({ templateProject: e.target.value })}
                          >
                            {this.state.templates.map((t) => (
                              <option key={t.project} value={t.project}>
                                {t.name}
                              </option>
                            ))}
                          </Input>
                        </div>
                      </div>
                    )}
                    <div>
                      <Button color="primary" disabled={busy} onClick={this.createBlank}>
                        Criar cronograma em branco
                      </Button>
                      {this.state.templates.length > 0 && (
                        <Button
                          color="secondary"
                          disabled={busy || !this.state.templateProject}
                          onClick={this.createFromTemplate}
                        >
                          Criar a partir do template selecionado
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          ) : (
            <>
              <Card className="mb-3">
                <CardBody className={headerCollapsed ? "py-2" : undefined}>
                  <div className="d-flex align-items-start justify-content-between">
                    {headerCollapsed ? (
                      <div>
                        <strong>{project.name}</strong>
                        <span className="text-muted"> — {project.customerShortName}</span>
                        <span className="badge badge-secondary ml-2">{version.statusGeral}</span>
                      </div>
                    ) : (
                      <div className="flex-grow-1">
                        <Row>
                          <Col md="2">
                            <small className="text-muted d-block">Projeto</small>
                            {project.name}
                          </Col>
                          <Col md="2">
                            <small className="text-muted d-block">Cliente</small>
                            {project.customerShortName}
                          </Col>
                          <Col md="2">
                            <small className="text-muted d-block">Data de Início do Projeto</small>
                            {canEdit ? (
                              <Input
                                bsSize="sm"
                                type="date"
                                defaultValue={toInputDate(version.startDate)}
                                onBlur={(e) => e.target.value && this.updateVersionField("startDate", e.target.value)}
                              />
                            ) : (
                              fmtDate(version.startDate)
                            )}
                          </Col>
                          <Col md="2">
                            <small className="text-muted d-block">Previsão de Término</small>
                            {fmtDate(root ? root.effectiveEnd : null)}
                          </Col>
                          {!isClient && (
                            <Col md="1">
                              <small className="text-muted d-block">Horas</small>
                              {root ? root.budgetedHours : 0}
                            </Col>
                          )}
                          {/* % Medido e Medido são derivados de Horas — ficam só pra consultoria. */}
                          {!isClient && (
                            <Col xs="auto">
                              <small className="text-muted d-block text-nowrap">% Medido</small>
                              {root ? Math.round(root.percentComplete) : 0}%
                            </Col>
                          )}
                          {!isClient && (
                            <Col xs="auto">
                              <small className="text-muted d-block text-nowrap">Medido</small>
                              {Math.round(medidoHoras)}
                            </Col>
                          )}
                          <Col xs="auto">
                            <small className="text-muted d-block text-nowrap">% Concluído</small>
                            {Math.round(percentByDuration)}%
                          </Col>
                        </Row>
                        <Row className="mt-3">
                          <Col md="4">
                            <small className="text-muted d-block">Revisão</small>
                            <Input
                              bsSize="sm"
                              type="select"
                              value={version._id}
                              onChange={(e) => this.handleSelectRevision(e.target.value)}
                            >
                              {revisions.map((v) => (
                                <option key={v._id} value={v._id}>
                                  {`Revisão ${v.revisionNumber}${revisions[0]._id === v._id ? " (atual)" : ""} — início ${fmtDate(v.startDate)}`}
                                </option>
                              ))}
                            </Input>
                          </Col>
                          <Col md="4">
                            <small className="text-muted d-block">Status Geral</small>
                            {/* Não é mais editável — reflexo das tarefas de execução, igual ao
                                status de cada tarefa (ver cronogramaEngine.computeStatusGeral). */}
                            {version.statusGeral}
                          </Col>
                          <Col md="4">
                            {!isCurrentVersion && (
                              <span className="badge badge-warning mr-2">
                                Revisão histórica — somente leitura
                              </span>
                            )}
                            <Button size="sm" color="secondary" className="mr-2" disabled={busy} onClick={this.printCronograma}>
                              Imprimir
                            </Button>
                            {isCurrentVersion && isConsulting() && (
                              <Button size="sm" color="secondary" disabled={busy} onClick={this.createNewRevision}>
                                Nova revisão
                              </Button>
                            )}
                          </Col>
                        </Row>
                      </div>
                    )}
                    <Button
                      size="sm"
                      color="link"
                      className="p-0 ml-3 text-nowrap"
                      onClick={() => this.setState((prev) => ({ headerCollapsed: !prev.headerCollapsed }))}
                      title={headerCollapsed ? "Expandir cabeçalho" : "Recolher cabeçalho — mais espaço pra grade"}
                    >
                      {headerCollapsed ? "▾ Expandir" : "▴ Recolher"}
                    </Button>
                  </div>
                </CardBody>
              </Card>

              {tasks.some((t) => t.isSection) && (
                <div className="mb-2 text-right">
                  <Button size="sm" color="secondary" outline className="mr-2" onClick={this.expandAll}>
                    Expandir tudo
                  </Button>
                  <Button size="sm" color="secondary" outline onClick={this.collapseAll}>
                    Recolher tudo
                  </Button>
                </div>
              )}

              <Card>
                {/* .form-control herda o font-size do container em vez do tamanho fixo que o
                    Bootstrap aplica por padrão — senão reduzir a fonte aqui não afetaria os
                    campos de edição, só o texto solto.
                    border-collapse: separate é o ajuste que resolve o "vazamento" na coluna
                    congelada (Cód/EAP/Tarefa) — com collapse (padrão do Bootstrap), o navegador
                    deixa uma costura de 1px na borda colada de célula sticky por onde o conteúdo
                    de trás aparece durante o scroll horizontal, mesmo com fundo sólido. */}
                <style>{`
                  .cronograma-grid .form-control { font-size: inherit; }
                  .cronograma-grid table { border-collapse: separate; border-spacing: 0; }
                `}</style>
                <CardBody
                  className="cronograma-grid"
                  style={{
                    padding: 0,
                    display: "flex",
                    flexDirection: "column",
                    // Cabeçalho recolhido libera mais altura de tela pra grade — só esse número
                    // muda, a rolagem sincronizada/sticky das colunas continua igual.
                    maxHeight: headerCollapsed ? "88vh" : "70vh",
                    fontSize: "80%",
                  }}
                >
                  {/* Cabeçalho: <table> própria, FORA do contêiner que rola verticalmente — não
                      usa position:sticky em eixo nenhum, então não tem onde reproduzir o bug do
                      Chrome (ver comentário grande perto de GPU_LAYER). overflow:hidden só corta
                      visualmente quando o corpo rola na horizontal; o scrollLeft dela é
                      empurrado por JS (handleBodyScroll) pra acompanhar o corpo. paddingRight
                      compensa a largura real da barra de rolagem vertical do corpo, pra as
                      colunas não saírem de alinhamento na ponta direita. */}
                  <div
                    ref={this.headerScrollRef}
                    style={{
                      overflow: "hidden",
                      flexShrink: 0,
                      borderBottom: "2px solid #dee2e6",
                      paddingRight: scrollbarWidth,
                    }}
                  >
                    <Table
                      size="sm"
                      className="align-items-center mb-0"
                      style={{ width: gridWidth, tableLayout: "fixed" }}
                    >
                      {this.renderColgroup(canEdit, isClient)}
                      <thead className="thead-light">
                        <tr>
                          <th
                            title="Código da linha — use em Pred 1/Pred 2 pra referenciar esta tarefa"
                            style={stickyColStyle(SEQ_COL_LEFT, SEQ_COL_WIDTH, HEADER_BG, { zIndex: 3 })}
                          >
                            Cód.
                          </th>
                          <th style={stickyColStyle(EAP_COL_LEFT, EAP_COL_WIDTH, HEADER_BG, { zIndex: 3 })}>
                            EAP
                          </th>
                          <th
                            style={stickyColStyle(TAREFA_COL_LEFT, TAREFA_COL_WIDTH, HEADER_BG, { zIndex: 3 })}
                          >
                            Tarefa
                          </th>
                          {!isClient && <th style={headerCellStyle()}>Horas</th>}
                          <th style={headerCellStyle()}>Duração</th>
                          <th style={headerCellStyle()}>Início</th>
                          <th style={headerCellStyle()}>Início Real</th>
                          <th style={headerCellStyle()}>Fim</th>
                          <th style={headerCellStyle()}>Fim Real</th>
                          <th style={headerCellStyle()}>% Concl.</th>
                          <th style={headerCellStyle()}>Status</th>
                          <th style={headerCellStyle()}>Observações</th>
                          <th style={headerCellStyle()}>Pred 1</th>
                          <th style={headerCellStyle()}>Lag 1</th>
                          <th style={headerCellStyle()}>Pred 2</th>
                          <th style={headerCellStyle()}>Lag 2</th>
                          {canEdit && <th style={headerCellStyle()}>Ações</th>}
                        </tr>
                      </thead>
                    </Table>
                  </div>
                  <div
                    ref={this.bodyScrollRef}
                    onScroll={this.handleBodyScroll}
                    style={{ overflow: "auto", flex: "1 1 auto", minHeight: 0 }}
                  >
                    <Table
                      size="sm"
                      className="align-items-center mb-0"
                      style={{ width: gridWidth, tableLayout: "fixed" }}
                    >
                      {this.renderColgroup(canEdit, isClient)}
                      <tbody>
                        {visibleTasks.map((task) =>
                          this.renderRow(task, childrenSeqs.has(task.seq), canEdit, isClient, {
                            start: forcedByAncestor(task, "realStartDate"),
                            end: forcedByAncestor(task, "realEndDate"),
                          }, durationPercents.get(task.seq))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </>
          )}
        </Container>
      </>
    )
  }

  // Compartilhado entre a <table> do cabeçalho e a <table> do corpo — as duas precisam do
  // MESMO <colgroup> pra as colunas ficarem alinhadas entre si (ver comentário de COL_WIDTHS).
  renderColgroup(canEdit, isClient) {
    return (
      <colgroup>
        <col style={{ width: SEQ_COL_WIDTH }} />
        <col style={{ width: EAP_COL_WIDTH }} />
        <col style={{ width: TAREFA_COL_WIDTH }} />
        {!isClient && <col style={{ width: COL_WIDTHS.horas }} />}
        <col style={{ width: COL_WIDTHS.duracao }} />
        <col style={{ width: COL_WIDTHS.inicio }} />
        <col style={{ width: COL_WIDTHS.inicioReal }} />
        <col style={{ width: COL_WIDTHS.fim }} />
        <col style={{ width: COL_WIDTHS.fimReal }} />
        <col style={{ width: COL_WIDTHS.percentConcl }} />
        <col style={{ width: COL_WIDTHS.status }} />
        <col style={{ width: COL_WIDTHS.observacoes }} />
        <col style={{ width: COL_WIDTHS.pred1 }} />
        <col style={{ width: COL_WIDTHS.lag1 }} />
        <col style={{ width: COL_WIDTHS.pred2 }} />
        <col style={{ width: COL_WIDTHS.lag2 }} />
        {canEdit && <col style={{ width: COL_WIDTHS.acoes }} />}
      </colgroup>
    )
  }

  renderRow(task, hasChildren, canEdit, isClient, forced = {}, durationPercent) {
    const depth = Math.max((task.eap || "").split(".").length - 1, 0)
    const isAtrasado = task.status === "Atrasado"
    // Atrasado tem prioridade visual sobre o azul de seção — é o sinal mais urgente da tela.
    const rowBg = isAtrasado ? "#f8d7da" : task.isSection ? "#eef4fb" : "#fff"
    const rowStyle = {
      backgroundColor: rowBg,
      fontWeight: task.isSection ? 600 : undefined,
      color: isAtrasado ? "#842029" : undefined,
    }
    const editableLeaf = canEdit && !task.isSection
    // Com as duas pontas manuais preenchidas (Início Real + Fim Real), OU numa seção que já
    // tem filhos (Fim dela é sempre o Fim mais tardio entre os filhos — cronogramaEngine.
    // resolveDates), a Duração vira reflexo calculado — editar aqui seria sobrescrito no
    // próximo recálculo, então a célula trava e só mostra o valor.
    const startPinned = forced.start || !!task.realStartDate
    const endPinned = forced.end || !!task.realEndDate
    const durationLocked = (task.isSection && hasChildren) || forced.end || (startPinned && endPinned)

    return (
      <tr key={task._id} style={rowStyle}>
        <td
          title="Código da linha — use em Pred 1/Pred 2 pra referenciar esta tarefa"
          style={stickyColStyle(SEQ_COL_LEFT, SEQ_COL_WIDTH, rowBg, { whiteSpace: "nowrap" })}
        >
          {task.seq}
        </td>
        <td style={stickyColStyle(EAP_COL_LEFT, EAP_COL_WIDTH, rowBg, { whiteSpace: "nowrap" })}>
          {task.eap}
        </td>
        <td style={stickyColStyle(TAREFA_COL_LEFT, TAREFA_COL_WIDTH, rowBg, { whiteSpace: "nowrap" })}>
          <span style={{ paddingLeft: depth * 16, display: "flex", alignItems: "center" }}>
            {hasChildren && (
              <span
                style={{ cursor: "pointer", marginRight: 4 }}
                onClick={() => this.toggleCollapse(task.seq)}
              >
                {this.state.collapsed[task.seq] ? "▸" : "▾"}
              </span>
            )}
            {canEdit && (
              <input
                type="checkbox"
                checked={!!task.isSection}
                title="Seção"
                style={{ marginRight: 4 }}
                onChange={(e) => this.saveTaskField(task, "isSection", e.target.checked)}
              />
            )}
            {canEdit ? (
              <Input
                bsSize="sm"
                defaultValue={task.name}
                onBlur={(e) => e.target.value !== task.name && this.saveTaskField(task, "name", e.target.value)}
              />
            ) : (
              task.name
            )}
          </span>
        </td>
        {!isClient && (
          <td>
            {editableLeaf ? (
              <Input
                bsSize="sm"
                type="number"
                style={{ width: 70 }}
                defaultValue={task.budgetedHours}
                onBlur={(e) => this.saveTaskField(task, "budgetedHours", Number(e.target.value) || 0)}
              />
            ) : (
              task.budgetedHours || 0
            )}
          </td>
        )}
        <td
          title={
            durationLocked
              ? task.isSection
                ? "Calculada a partir do Início e do Fim da seção"
                : "Calculada a partir de Início e Fim fixados"
              : undefined
          }
        >
          {canEdit && !durationLocked ? (
            <Input
              bsSize="sm"
              type="number"
              style={{ width: 60 }}
              defaultValue={task.durationDays ?? ""}
              onBlur={(e) =>
                this.saveTaskField(task, "durationDays", e.target.value === "" ? null : Number(e.target.value))
              }
            />
          ) : (
            (durationLocked ? task.effectiveDuration ?? task.durationDays : task.durationDays) ?? "-"
          )}
        </td>
        <td>{fmtDate(task.effectiveStart)}</td>
        {/* Numa seção, Início/Fim Real são adotados por TODAS as tarefas abaixo (ignorando
            predecessoras, lags e o Início/Fim Real delas). A seção mais externa vence — por isso
            a célula de quem está sob uma seção com a data preenchida fica travada. */}
        <td title={forced.start ? "Início definido por uma seção acima" : undefined}>
          {forced.start ? (
            "-"
          ) : canEdit ? (
            <Input
              bsSize="sm"
              type="date"
              defaultValue={toInputDate(task.realStartDate)}
              onBlur={(e) => this.saveTaskField(task, "realStartDate", e.target.value || null)}
            />
          ) : (
            fmtDate(task.realStartDate)
          )}
        </td>
        <td>{fmtDate(task.effectiveEnd)}</td>
        <td title={forced.end ? "Fim definido por uma seção acima" : undefined}>
          {forced.end ? (
            "-"
          ) : canEdit ? (
            <Input
              bsSize="sm"
              type="date"
              defaultValue={toInputDate(task.realEndDate)}
              onBlur={(e) => this.saveTaskField(task, "realEndDate", e.target.value || null)}
            />
          ) : (
            fmtDate(task.realEndDate)
          )}
        </td>
        <td>
          {editableLeaf ? (
            // Texto numérico (sem as setinhas do type="number"), só dígitos e no máximo 100.
            <Input
              bsSize="sm"
              type="text"
              inputMode="numeric"
              maxLength={3}
              style={{ width: 60 }}
              defaultValue={task.percentComplete}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "")
                e.target.value = digits !== "" && Number(digits) > 100 ? "100" : digits
              }}
              onBlur={(e) => {
                const pct = Math.min(100, Math.max(0, Number(e.target.value) || 0))
                e.target.value = String(pct)
                if (pct !== (task.percentComplete || 0)) this.saveTaskField(task, "percentComplete", pct)
              }}
            />
          ) : (
            // Seção: % por Duração (ver computeDurationPercents), não o rollup por Horas.
            `${Math.round(durationPercent ?? task.percentComplete ?? 0)}%`
          )}
        </td>
        {/* Status não é mais digitado — é sempre reflexo de % Conclusão x Data Fim (usar),
            recalculado no backend (cronogramaEngine.computeStatus). */}
        <td>{task.status}</td>
        <td>
          {canEdit ? (
            <Input
              bsSize="sm"
              defaultValue={task.notes || ""}
              onBlur={(e) => this.saveTaskField(task, "notes", e.target.value)}
            />
          ) : (
            task.notes || ""
          )}
        </td>
        <td>
          {canEdit ? (
            <Input
              bsSize="sm"
              type="number"
              style={{ width: 60 }}
              defaultValue={task.pred1 ?? ""}
              onBlur={(e) => this.saveTaskField(task, "pred1", e.target.value === "" ? null : Number(e.target.value))}
            />
          ) : (
            task.pred1 ?? "-"
          )}
        </td>
        <td>
          {canEdit ? (
            <Input
              bsSize="sm"
              type="number"
              style={{ width: 50 }}
              defaultValue={task.lag1 || 0}
              onBlur={(e) => this.saveTaskField(task, "lag1", Number(e.target.value) || 0)}
            />
          ) : (
            task.lag1 || 0
          )}
        </td>
        <td>
          {canEdit ? (
            <Input
              bsSize="sm"
              type="number"
              style={{ width: 60 }}
              defaultValue={task.pred2 ?? ""}
              onBlur={(e) => this.saveTaskField(task, "pred2", e.target.value === "" ? null : Number(e.target.value))}
            />
          ) : (
            task.pred2 ?? "-"
          )}
        </td>
        <td>
          {canEdit ? (
            <Input
              bsSize="sm"
              type="number"
              style={{ width: 50 }}
              defaultValue={task.lag2 || 0}
              onBlur={(e) => this.saveTaskField(task, "lag2", Number(e.target.value) || 0)}
            />
          ) : (
            task.lag2 || 0
          )}
        </td>
        {canEdit && (
          <td style={{ whiteSpace: "nowrap" }}>
            <Button size="sm" color="link" className="p-1" title="Mover para cima" onClick={() => this.moveTask(task, "up")}>
              ↑
            </Button>
            <Button size="sm" color="link" className="p-1" title="Mover para baixo" onClick={() => this.moveTask(task, "down")}>
              ↓
            </Button>
            <Button size="sm" color="link" className="p-1" title="Inserir linha abaixo" onClick={() => this.insertTaskAfter(task)}>
              +
            </Button>
            <Button size="sm" color="link" className="p-1 text-danger" title="Remover" onClick={() => this.deleteTask(task)}>
              ×
            </Button>
          </td>
        )}
      </tr>
    )
  }
}

export default CronogramaConsole
